import { createContext, type ReactNode, useContext } from "react";
import { Copy, type CopyKey } from "@/copy/copy";

export type OnbNav = {
  /** Go to a step by id. */
  go: (id: string) => void;
  back: () => void;
  /** Validate this step's fields, then go to the next step. */
  next: () => Promise<void>;
  stepId: string;
};

export const OnbNavContext = createContext<OnbNav | null>(null);

export function useOnbNav(): OnbNav {
  const ctx = useContext(OnbNavContext);
  if (!ctx) throw new Error("useOnbNav must be used inside the onboarding");
  return ctx;
}

/**
 * One onboarding screen in the prototype's markup: `.screen.on > .narrow` with eyebrow, h1, sub,
 * the body, and the Back / Continue row.
 */
export function Screen({
  eyebrow,
  h1,
  sub,
  children,
  nextLabel = "Continue",
  onNext,
  hideBack = false,
  maxWidth,
  nextDisabled = false,
  nextTestId = "onb-next",
}: {
  eyebrow?: ReactNode;
  h1: CopyKey;
  sub?: CopyKey;
  children?: ReactNode;
  nextLabel?: ReactNode;
  onNext?: () => void | Promise<void>;
  hideBack?: boolean;
  maxWidth?: number;
  nextDisabled?: boolean;
  nextTestId?: string;
}) {
  const nav = useOnbNav();
  return (
    <section className="screen on" data-testid={`onb-${nav.stepId}`}>
      <div className="narrow" style={maxWidth ? { maxWidth } : undefined}>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <Copy k={h1} as="h1" />
        {sub && <Copy k={sub} as="p" className="sub" />}
        {children}
        <div className="nav">
          {!hideBack && (
            <button type="button" className="btn g" onClick={nav.back} data-testid="onb-back">
              Back
            </button>
          )}
          <button
            type="button"
            className="btn p sp"
            onClick={() => void (onNext ? onNext() : nav.next())}
            disabled={nextDisabled}
            data-testid={nextTestId}
          >
            {nextLabel}
          </button>
        </div>
      </div>
    </section>
  );
}
